import { useState, useCallback } from "react";
import api, { getFreshAccessToken } from "../chat/api";
import toast from "react-hot-toast";

// NOTE: this used to take (setFile, setIsModalOpen, …) so picked files could be
// pushed into UploadModal. It has driven its own progress overlay for a while and
// never called either setter, so the dead params were dropped.
export default function useSalesforcePicker(refreshRfpDocuments, targetType = "rfp") {
  const [isSFDCConnected, setIsSFDCConnected] = useState(false);
  const [sfdcFiles, setSfdcFiles] = useState([]);
  const [isFetchingSFDCFiles, setIsFetchingSFDCFiles] = useState(false);
  const [showSFDCFileList, setShowSFDCFileList] = useState(false);

  // 🔄 New: live upload overlay state
  const [isSFDCUploading, setIsSFDCUploading] = useState(false);
  const [sfdcUploadItems, setSfdcUploadItems] = useState([]); 
  // each item: { id, title, pdf_key, progress, status }

  // -------------------------------------------------
  // 1. Verify Salesforce connection
  // -------------------------------------------------
  const verifySFDCConnection = async () => {
    try {
      const res = await api.get("/api/v1/accounts/sfdc-connect/");
      if (res.data.status === "connected" || res.data.instance_url) {
        setIsSFDCConnected(true);
        return true;
      }
      setIsSFDCConnected(false);
      return false;
    } catch (err) {
      console.error("Error verifying Salesforce connection:", err);
      setIsSFDCConnected(false);
      return false;
    }
  };

  // -------------------------------------------------
  // 2. Connect to Salesforce (OAuth popup)
  // -------------------------------------------------
  const handleConnectSFDC = async () => {
    try {
      const res = await api.get("/api/v1/accounts/sfdc-connect/");

      // already connected
      if (res.data.instance_url || res.data.status === "connected") {
        setIsSFDCConnected(true);
        return;
      }

      // need auth
      if (res.data.authorization_url) {
        const popup = window.open(
          res.data.authorization_url,
          "SalesforceConnect",
          "width=600,height=700,left=400,top=100"
        );

        if (!popup) {
          toast.error("Please allow popups for this site.");
          return;
        }

        const popupTimer = setInterval(async () => {
          if (popup.closed) {
            clearInterval(popupTimer);

            const ok = await verifySFDCConnection();
            if (ok) {
              setIsSFDCConnected(true);
            } else {
              toast.error("Salesforce connection failed. Try again.");
            }
          }
        }, 2000);
      } else {
        toast.error("No authorization URL returned.");
      }
    } catch (err) {
      console.error("Error connecting to Salesforce:", err);
      toast.error("Failed to connect to Salesforce.");
    }
  };

  const handleDisconnectSFDC = async () => {
  try {
    const res = await api.post(
      "/api/v1/accounts/sfdc-connect/disconnect/"
    );
    toast.success("Salesforce disconnected!");

    setIsSFDCConnected(false);
  } catch (err) {
    console.error("Error disconnecting Salesforce:", err);
    toast.error("Failed to disconnect Salesforce.");
  }
};

  // -------------------------------------------------
  // 3. Fetch list of Salesforce files (for picker modal)
  // -------------------------------------------------
  const handleFetchSFDCFiles = async () => {
    try {
      setIsFetchingSFDCFiles(true);
      const res = await api.get("/api/v1/accounts/sfdc/files/");
      const files = Array.isArray(res.data.files) ? res.data.files : [];
      setSfdcFiles(files);
      setShowSFDCFileList(true);
    } catch (err) {
      console.error("Error fetching SFDC files:", err);
      toast.error("Failed to fetch Salesforce files. Please ensure you're connected.");
    } finally {
      setIsFetchingSFDCFiles(false);
    }
  };

  // -------------------------------------------------
  // helper: check if all uploads in overlay are finished
  // -------------------------------------------------
  const allDone = (items) =>
    items.length > 0 &&
    items.every(
      (it) =>
        it.status === "completed" ||
        it.status === "error" ||
        it.status === "failed"
    );

  // -------------------------------------------------
  // 4. Handle SFDC file selection -> backend ingest -> live progress
  // -------------------------------------------------
const handleSelectSFDCFiles = async (selected) => {
  console.log("🟣 [SFDC Picker] Selected files for RFP upload:", selected);

  try {
    if (!selected.length) {
      toast("No files selected.", { icon: "ℹ️" });
      return;
    }

    // Close Salesforce file list modal
    setShowSFDCFileList(false);

    // Show overlay
    setIsSFDCUploading(true);
    setSfdcUploadItems(
      selected.map((f) => ({
        id: f.id,
        title: f.title,
        pdf_key: null,
        progress: 0,
        status: "uploading",
      }))
    );

    // open WebSocket listener for live progress BEFORE triggering backend upload
    const token = await getFreshAccessToken();
    const wsUrl = `${process.env.REACT_APP_WS_URL}/ws/upload-status/?token=${token}`;
    console.log("🌐 Opening WebSocket connection:", wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => console.log("✅ [SFDC Upload WS] Connected");
    ws.onclose = (e) =>
      console.log("⚪ [SFDC Upload WS] Closed:", e.code, e.reason);
    ws.onerror = (err) => console.error("🔴 [SFDC Upload WS] Error:", err);

    ws.onmessage = (e) => {
      const d = JSON.parse(e.data);
      console.log("📨 [SFDC Upload WS] Incoming message:", d);

      setSfdcUploadItems((prev) => {
        let claimed = false;

        const updated = prev.map((item) => {
          const isMatchExisting =
            item.pdf_key && item.pdf_key === d.pdf_key;
          const canClaimNow =
            !isMatchExisting &&
            !item.pdf_key &&
            !claimed &&
            item.status === "uploading" &&
            d.pdf_key;

          if (!isMatchExisting && !canClaimNow) return item;

          const patch = {};

          // attach pdf_key first time we recognize it
          if (canClaimNow) {
            patch.pdf_key = d.pdf_key;
            claimed = true;
            console.log("🔗 [SFDC Upload WS] Claimed pdf_key for", item.title, d.pdf_key);
          }

          if (d.type === "progress") {
            patch.progress = d.progress ?? item.progress;
            patch.status = "uploading";
          } 
          else if (d.type === "completed") {
            // 🧩 Ignore premature completions (no metadata yet)
            if (!d.metadata?.file_id) {
              console.warn("⚠️ [SFDC Upload] Ignoring early completed event (no metadata):", d);
              return item; // don’t update UI yet
            }

            patch.progress = 100;
            patch.status = "completed";
            console.log("🎉 [SFDC Upload] Completed upload for:", item.title, d.metadata);

            // ✅ Toast once when final metadata is present

            // ✅ Refresh table after a small delay
            if (typeof refreshRfpDocuments === "function") {
              console.log("🔁 [SFDC Upload] Refreshing RFP table…");
              setTimeout(() => refreshRfpDocuments(), 500);
            }
          } 
          else if (d.type === "error") {
            patch.status = "error";
            console.error("❌ [SFDC Upload WS] Error event:", d);
            toast.error(`❌ ${item.title} upload failed: ${d.message || "Unknown error"}`);
          }

          return { ...item, ...patch };
        });

        // Check if all done
        const done = updated.every(
          (it) => it.status === "completed" || it.status === "error"
        );
        if (done && ws.readyState === WebSocket.OPEN) {
          console.log("✅ [SFDC Upload] All uploads completed, closing socket.");
          ws.close();
          setTimeout(() => setIsSFDCUploading(false), 1000);
        }

        return updated;
      });
    };

    // Trigger backend ingestion for each selected file
    for (const file of selected) {
const downloadUrl = `/api/v1/accounts/sfdc/files/download/${file.id}/?target=${targetType}`;
      console.log(`🚀 [SFDC → DocuSensa] Starting upload for ${file.title} via ${downloadUrl}`);

      try {
        const res = await api.get(downloadUrl);
        console.log("📦 [SFDC → DocuSensa] Backend response:", res.data);

        if (res.status === 200 || res.status === 201) {
  const result = res.data?.results?.[0];

  // Handle duplicate or failed cases
  if (result?.status === "failed" && result?.reason) {
    console.warn(`⚠️ [SFDC Upload] File failed: ${result.reason}`);
    toast.error(`❌ ${file.title}: ${result.reason}`);

    // Update state to reflect failure
    setSfdcUploadItems((prev) =>
      prev.map((row) =>
        row.id === file.id
          ? { ...row, status: "error", reason: result.reason }
          : row
      )
    );
    return;
  }

  // ✅ Normal successful upload start
  console.log("✅ [SFDC Upload] Upload successfully initiated:", res.data);
} else {
  console.warn("⚠️ [SFDC Upload] Unexpected status code:", res.status, res.data);
  toast.error(`Failed to start upload for ${file.title}`);
  setSfdcUploadItems((prev) =>
    prev.map((row) =>
      row.id === file.id ? { ...row, status: "error" } : row
    )
  );
}

      } catch (err) {
        console.error(`💥 [SFDC Upload] Exception while starting upload for ${file.title}:`, err);
        toast.error(`Error uploading ${file.title}`);
        setSfdcUploadItems((prev) =>
          prev.map((row) =>
            row.id === file.id ? { ...row, status: "error" } : row
          )
        );
      }
    }
  } catch (err) {
    console.error("🔥 [SFDC → DocuSensa] Overall upload error:", err);
    toast.error("Failed to upload Salesforce files to RFP.");
    setIsSFDCUploading(false);
  }
};


  return {
    // existing stuff
    isSFDCConnected,
    sfdcFiles,
    isFetchingSFDCFiles,
    showSFDCFileList,
    handleConnectSFDC,
    handleFetchSFDCFiles,
    handleSelectSFDCFiles,
    verifySFDCConnection,
    setShowSFDCFileList,
handleDisconnectSFDC,
    // new stuff (for overlay UI)
    isSFDCUploading,
    sfdcUploadItems,
  };
}
